import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Upload, FileScan, Loader2, CheckCircle2, Plus, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [extracted, setExtracted] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/invoices/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(r.data);
      const a = r.data.auto_update || {};
      const parts = [];
      if (a.supplier_created) parts.push("fornitore creato");
      else if (a.supplier_updated) parts.push("listino fornitore aggiornato");
      if (a.ingredients_created) parts.push(`${a.ingredients_created} ingredienti nuovi`);
      if (a.ingredients_updated) parts.push(`${a.ingredients_updated} prezzi aggiornati`);
      if (a.dishes_recalculated) parts.push("piatti ricalcolati");
      toast.success(parts.length ? `Fattura analizzata · ${parts.join(", ")}` : "Fattura analizzata");
      load();
    } catch (e) {
      toast.error("Errore OCR: " + (e?.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  const createSupplier = async () => {
    if (!extracted?.invoice?.id) return;
    try {
      await api.post(`/invoices/${extracted.invoice.id}/create-supplier`);
      toast.success("Fornitore creato dal listino estratto");
      setExtracted(null);
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Amministrazione</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">OCR Fatture / DDT</h1>
        <p className="text-slate-500 mt-2">Carica una fattura o DDT (foto o PDF) e l'AI estrarrà fornitore, ingredienti e prezzi.</p>
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-[#EFF6FF]"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("bg-[#EFF6FF]")}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("bg-[#EFF6FF]"); onFile(e.dataTransfer.files?.[0]); }}
        className="border-2 border-dashed border-[#2563EB]/30 rounded-xl p-12 text-center cursor-pointer transition-colors hover:border-[#2563EB]"
        data-testid="ocr-dropzone"
      >
        <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => onFile(e.target.files?.[0])} />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-12 h-12 text-[#2563EB] animate-spin" />
            <p className="text-sm text-slate-600">Analisi AI in corso...</p>
          </div>
        ) : (
          <div>
            <div className="w-16 h-16 rounded-full bg-[#EFF6FF] mx-auto grid place-items-center mb-4">
              <Upload className="w-7 h-7 text-[#2563EB]" />
            </div>
            <p className="font-ui font-semibold text-lg text-[#0F2741]">Trascina un file o clicca per caricare</p>
            <p className="text-xs text-slate-500 mt-2">Formati: JPG, PNG, WEBP, PDF · Max 10MB</p>
          </div>
        )}
      </div>

      {extracted && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="ocr-result">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <h3 className="font-ui font-semibold text-lg text-[#0F2741]">Dati estratti</h3>
            </div>
            <button onClick={createSupplier} className="btn-primary"><Plus className="w-4 h-4" /> Crea fornitore</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Fornitore</p><p className="font-semibold text-[#0F2741] mt-1">{extracted.parsed.supplier_name || "—"}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">P.IVA</p><p className="font-semibold text-[#0F2741] mt-1">{extracted.parsed.supplier_vat || "—"}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Totale</p><p className="font-display text-xl text-[#2563EB] mt-1">€ {Number(extracted.parsed.total || 0).toFixed(2)}</p></div>
          </div>
          {extracted.auto_update && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm" data-testid="ocr-auto-update">
              <p className="font-semibold text-emerald-800 mb-1">Aggiornamenti automatici</p>
              <ul className="text-emerald-700 text-xs space-y-0.5">
                {extracted.auto_update.supplier_created && <li>✓ Nuovo fornitore "{extracted.parsed.supplier_name}" creato</li>}
                {extracted.auto_update.supplier_updated && <li>✓ Listino prezzi del fornitore aggiornato</li>}
                {extracted.auto_update.ingredients_created > 0 && <li>✓ {extracted.auto_update.ingredients_created} nuovi ingredienti aggiunti al DB</li>}
                {extracted.auto_update.ingredients_updated > 0 && <li>✓ {extracted.auto_update.ingredients_updated} prezzi ingredienti aggiornati</li>}
                {extracted.auto_update.dishes_recalculated && <li>✓ Food cost e prezzi di vendita dei piatti ricalcolati</li>}
              </ul>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-3 py-2">Ingrediente</th><th className="text-right px-3 py-2">Qtà (kg)</th><th className="text-right px-3 py-2">€/kg</th><th className="text-right px-3 py-2">Totale</th></tr></thead>
            <tbody>
              {(extracted.parsed.items || []).map((it, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 capitalize">{it.name}</td>
                  <td className="px-3 py-2 text-right">{it.quantity_kg || "—"}</td>
                  <td className="px-3 py-2 text-right">€{Number(it.unit_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-semibold">€{Number(it.total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h2 className="font-ui font-semibold text-lg text-[#0F2741] mb-3">Storico fatture caricate</h2>
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {invoices.length === 0 ? (
            <div className="p-8 text-center text-slate-400"><FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />Nessuna fattura caricata</div>
          ) : invoices.map((inv) => (
            <div key={inv.id} className="px-6 py-4 flex justify-between items-center">
              <div>
                <p className="font-semibold text-[#0F2741]">{inv.supplier_name || "Senza fornitore"}</p>
                <p className="text-xs text-slate-500">{(inv.items || []).length} righe · {new Date(inv.created_at).toLocaleDateString("it-IT")}</p>
              </div>
              <p className="font-display text-xl text-[#0F2741]">€ {Number(inv.total).toFixed(2)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
